import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Loader2, Plus, Search, Store, X } from "lucide-react";
import { AppDispatch, RootState } from "../../../../../dataManagement/uiDb/UiDb";
import { businessSearch } from "../../../../../services/thunks/BusinessThunk";
import { BusinessProfile } from "../../../../../interfaces/BusinessObject";
import { useDebounce } from "../../../../../hooks/useDebounce";
import { useBusinessProfilePicture } from "../../../../../hooks/useBusinessProfilePicture";
import "./styles/BusinessSearchAdd.css";

/** Thumbnail using the same canonical logo-URL resolution as BusinessProfileIcon,
 *  instead of reading business.logoUrl directly — a compact row here, not the
 *  full name+logo layout BusinessProfileIcon renders, so the hook is reused
 *  directly rather than the component. */
const BusinessResultThumb: React.FC<{ business: BusinessProfile }> = ({ business }) => {
  const logoUrl = useBusinessProfilePicture(business.businessId, business.logoUrl);
  return (
    <span className="biz-search-add-result-thumb">
      {logoUrl ? <img src={logoUrl} alt="" /> : <Store size={16} color="#6a11cb" />}
    </span>
  );
};

interface BusinessSearchAddProps {
  selected: BusinessProfile[];
  onAdd: (business: BusinessProfile) => void;
  onRemove: (businessId: string) => void;
}

/**
 * "Add businesses" step of Create/Edit Collection: debounced name search
 * (reuses the same businessSearch thunk SearchPage's Business scope uses,
 * but keeps results local instead of writing to the shared search slice —
 * this modal shouldn't clobber whatever the user has open on /search).
 * Tapping a result adds it; the running list below shows x-to-remove chips.
 */
const BusinessSearchAdd: React.FC<BusinessSearchAddProps> = ({
  selected,
  onAdd,
  onRemove,
}) => {
  const dispatch: AppDispatch = useDispatch();
  const userPreferences = useSelector(
    (state: RootState) => state.profile.userPreferences
  );

  const [query, setQuery] = useState("");
  const debouncedQuery = useDebounce(query, 350);
  const [results, setResults] = useState<BusinessProfile[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const trimmed = debouncedQuery.trim();
    if (!trimmed) {
      setResults([]);
      setError(null);
      return;
    }

    let cancelled = false;
    setIsSearching(true);
    setError(null);
    dispatch(businessSearch({ searchString: trimmed, userPreferences }))
      .unwrap()
      .then((res) => {
        if (!cancelled) setResults(res);
      })
      .catch((err) => {
        if (!cancelled) {
          setResults([]);
          setError(err instanceof Error ? err.message : "Search failed.");
        }
      })
      .finally(() => {
        if (!cancelled) setIsSearching(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQuery]);

  const selectedIds = new Set(selected.map((b) => b.businessId));

  return (
    <div className="biz-search-add">
      <div className="biz-search-add-input-row">
        <Search size={16} className="biz-search-add-icon" />
        <input
          type="text"
          className="biz-search-add-input"
          placeholder="Search businesses by name..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        {isSearching && <Loader2 size={16} className="biz-search-add-spinner" />}
      </div>

      {error && <p className="biz-search-add-error">{error}</p>}

      {query.trim() && (
        <div className="biz-search-add-results">
          {results.length === 0 && !isSearching && !error && (
            <p className="biz-search-add-empty">No businesses found.</p>
          )}
          {results.map((business) => {
            const alreadyAdded = selectedIds.has(business.businessId);
            return (
              <button
                type="button"
                key={business.businessId}
                className={`biz-search-add-result ${alreadyAdded ? "is-added" : ""}`}
                disabled={alreadyAdded}
                onClick={() => onAdd(business)}
              >
                <BusinessResultThumb business={business} />
                <span className="biz-search-add-result-name">{business.name}</span>
                {alreadyAdded ? (
                  <span className="biz-search-add-result-added">Added</span>
                ) : (
                  <Plus size={16} className="biz-search-add-result-plus" />
                )}
              </button>
            );
          })}
        </div>
      )}

      {selected.length > 0 && (
        <div className="biz-search-add-chips">
          {selected.map((business) => (
            <span className="biz-search-add-chip" key={business.businessId}>
              {business.name}
              <button
                type="button"
                aria-label={`Remove ${business.name}`}
                onClick={() => onRemove(business.businessId)}
              >
                <X size={12} />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default BusinessSearchAdd;
